import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from './services/api';

function Register() {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '',
    kit_number: '', wing: '', entry_year: '',
    contact_no: '', cnic: '', entry_batch: '' 
  });
  
  const [passwordError, setPasswordError] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  // Grouped state for all payment variables from Admin
  const [paymentDetails, setPaymentDetails] = useState({
      fee: '350',
      number: 'Loading...',
      title: 'Loading...'
  });
  
  const navigate = useNavigate();

  // Fetch dynamic payment group from backend on mount
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        const response = await API.get('/get_settings.php?group=payment');
        if (response.data && response.data.success) {
          setPaymentDetails(response.data.data);
        }
      } catch (error) {
        console.warn("Could not fetch payment details, falling back to defaults.", error);
      }
    };
    fetchPaymentDetails();
  }, []);

  const validatePassword = (password) => {
    if (password.length < 8) return "Must be at least 8 characters long.";
    if (!/[A-Z]/.test(password)) return "Must contain at least one uppercase letter.";
    if (!/[a-z]/.test(password)) return "Must contain at least one lowercase letter.";
    if (!/[0-9]/.test(password)) return "Must contain at least one number.";
    return ""; 
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'password') {
        setPasswordError('');
    }
  };

  const handleFileChange = (e) => {
      setScreenshot(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
        const pwdError = validatePassword(formData.password);
        if (pwdError) {
            setPasswordError(pwdError);
            return; 
        }
        setStep(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
    }

    if (!screenshot) {
        alert("Please attach your Easypaisa payment screenshot to proceed.");
        return;
    }

    setLoading(true);

    const submissionData = new FormData();
    Object.keys(formData).forEach(key => {
        submissionData.append(key, formData[key]);
    });
    submissionData.append('payment_screenshot', screenshot);

    try {
      const response = await API.post('/register.php', submissionData, {
          headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data && response.data.message) {
          alert(response.data.message);
          navigate('/login');
      } else {
          alert("Registration successful! Your account is pending admin verification.");
          navigate('/login');
      }
    } catch (error) {
      console.error("Registration failed!", error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || "Error connecting to the server.";
      alert("Registration failed: " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-wrapper">
      <style>{`
        .register-wrapper {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(-45deg, #134628, #0a2415, #1b6339, #134628);
            background-size: 400% 400%;
            animation: gradientBG 15s ease infinite;
            padding: 120px 20px 60px; 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .register-box {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            width: 100%;
            max-width: 650px;
            border-radius: 24px;
            box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
            overflow: hidden;
            border: 1px solid rgba(255, 255, 255, 0.2);
            animation: slideUpFade 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
        }

        .login-header-section {
            background: linear-gradient(135deg, #134628 0%, #1c663a 100%);
            color: white;
            padding: 40px 20px 30px;
            text-align: center;
            position: relative;
        }

        .login-header-section::after {
            content: '';
            position: absolute;
            bottom: 0; left: 0; right: 0;
            height: 5px;
            background: linear-gradient(90deg, #ffc300, #ffb703);
        }

        .login-logo-circle {
            width: 70px;
            height: 70px;
            background: linear-gradient(135deg, #ffc300 0%, #ffb703 100%);
            color: #134628;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 15px;
            font-size: 1.8rem;
            box-shadow: 0 10px 25px rgba(255, 195, 0, 0.4);
            animation: pulseGlow 2s infinite;
        }

        .login-header-section h2 {
            font-weight: 900;
            letter-spacing: 2px;
            margin: 0 0 5px 0;
            font-size: 1.6rem;
        }
        
        .login-header-section p {
            color: #a0c4ff;
            margin: 0;
            font-size: 0.95rem;
            font-weight: 300;
        }

        .login-form {
            padding: 35px 40px 25px;
        }

        .form-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
        }

        .full-width {
            grid-column: span 2;
        }

        .input-group-custom {
            position: relative;
        }

        .input-group-custom label {
            display: block;
            margin-bottom: 8px;
            font-weight: 700;
            color: #134628;
            font-size: 0.9rem;
        }

        .input-wrapper {
            position: relative;
            display: flex;
            align-items: center;
        }

        .input-wrapper i {
            position: absolute;
            left: 15px;
            color: #a0aab5;
            font-size: 1rem;
            transition: color 0.3s ease;
        }

        .input-group-custom input,
        .input-group-custom select {
            width: 100%;
            padding: 12px 15px 12px 42px; 
            border: 2px solid #e1e4e8;
            border-radius: 12px;
            font-size: 0.95rem;
            background: #fdfdfd;
            color: #333;
            transition: all 0.3s ease;
            appearance: none; 
        }

        .input-error {
            border-color: #d90429 !important;
            background: #fff0f3 !important;
        }

        .input-group-custom select {
            background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23a0aab5%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
            background-repeat: no-repeat;
            background-position: right 15px top 50%;
            background-size: 12px auto;
        }

        .input-group-custom input:focus,
        .input-group-custom select:focus {
            border-color: #ffc300;
            outline: none;
            background: #ffffff;
            box-shadow: 0 0 0 4px rgba(255, 195, 0, 0.15);
        }

        .input-group-custom input:focus + i,
        .input-group-custom select:focus + i,
        .input-wrapper:focus-within i {
            color: #ffc300;
        }
        
        .password-error-text {
            color: #d90429;
            font-size: 0.85rem;
            margin-top: 8px;
            font-weight: 700;
            display: flex;
            align-items: center;
            gap: 5px;
            animation: slideUpFade 0.3s ease;
        }

        .payment-box {
            position: relative;
            background: rgba(255, 195, 0, 0.05);
            border: 2px dashed rgba(255, 195, 0, 0.4);
            border-radius: 16px;
            padding: 35px 25px 25px; 
            margin-top: 15px;
            animation: slideUpFade 0.4s ease;
        }

        .premium-price-badge {
            position: absolute;
            top: -20px;
            right: -15px;
            background: linear-gradient(135deg, #ffc300 0%, #e6b000 100%);
            color: #001d3d;
            font-weight: 800;
            padding: 8px 20px;
            border-radius: 30px;
            box-shadow: 0 8px 20px rgba(255, 195, 0, 0.4);
            font-size: 1.2rem;
            border: 4px solid #ffffff; 
            z-index: 10;
            transform: rotate(3deg);
            transition: transform 0.3s ease;
        }

        .premium-price-badge:hover {
            transform: scale(1.05) rotate(0deg);
        }

        .premium-price-badge .currency {
            font-size: 0.8rem;
            vertical-align: middle;
            font-weight: 700;
            margin-right: 4px;
            text-transform: uppercase;
        }

        .login-submit-btn {
            width: 100%;
            background: linear-gradient(45deg, #ffc300, #ffb703);
            color: #134628;
            border: none;
            padding: 16px;
            border-radius: 12px;
            font-weight: 800;
            font-size: 1.1rem;
            cursor: pointer;
            transition: all 0.4s ease;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-top: 25px;
            box-shadow: 0 8px 20px rgba(255, 195, 0, 0.3);
        }

        .login-submit-btn:hover:not(:disabled) {
            transform: translateY(-3px);
            box-shadow: 0 15px 30px rgba(255, 195, 0, 0.5);
            background: linear-gradient(45deg, #ffb703, #ffc300);
        }

        .login-submit-btn:disabled {
            background: #e1e4e8;
            color: #a0aab5;
            cursor: not-allowed;
            box-shadow: none;
        }
        
        .btn-back {
            width: 100%;
            background: transparent;
            color: #134628;
            border: 2px solid #e1e4e8;
            padding: 14px;
            border-radius: 12px;
            font-weight: 700;
            cursor: pointer;
            transition: 0.3s;
            margin-top: 15px;
            text-transform: uppercase;
            letter-spacing: 1px;
        }
        
        .btn-back:hover {
            background: #f8fafc;
            border-color: #134628;
        }

        .login-footer {
            text-align: center;
            padding: 0 30px 40px;
            font-size: 0.95rem;
            color: #666;
        }

        .login-footer a {
            color: #134628;
            font-weight: 800;
            margin-left: 8px;
            text-decoration: none;
            transition: color 0.3s;
            border-bottom: 2px solid transparent;
        }

        .login-footer a:hover {
            color: #ffc300;
            border-bottom-color: #ffc300;
        }

        @keyframes gradientBG {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
        }

        @keyframes slideUpFade {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulseGlow {
            0% { box-shadow: 0 0 0 0 rgba(255, 195, 0, 0.6); }
            70% { box-shadow: 0 0 0 15px rgba(255, 195, 0, 0); }
            100% { box-shadow: 0 0 0 0 rgba(255, 195, 0, 0); }
        }

        @media (max-width: 600px) {
            .form-grid { grid-template-columns: 1fr; gap: 15px; }
            .full-width { grid-column: span 1; }
            .register-wrapper { padding: 100px 15px 40px; }
            .register-box { border-radius: 20px; }
            .login-header-section { padding: 30px 15px 25px; }
            .login-logo-circle { width: 60px; height: 60px; font-size: 1.5rem; }
            .login-form { padding: 25px 20px 20px; }
            .login-submit-btn { margin-top: 20px; }
            .premium-price-badge { right: -5px; top: -15px; border-width: 2px; }
        }
      `}</style>

      <div className="register-box">
        <div className="login-header-section">
          <div className="login-logo-circle">
            {step === 1 ? <i className="fas fa-user-plus"></i> : <i className="fas fa-file-invoice-dollar"></i>}
          </div>
          <h2>{step === 1 ? 'CADET REGISTRATION' : 'SECURE YOUR SPOT'}</h2>
          <p>{step === 1 ? 'Join the global Sherian network' : `Almost there, ${formData.name.split(' ')[0] || 'Sherian'}!`}</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          
          {step === 1 && (
              <>
                <div className="form-grid">
                    <div className="input-group-custom full-width">
                        <label htmlFor="name">Full Name</label>
                        <div className="input-wrapper">
                            <i className="fas fa-user"></i>
                            <input id="name" type="text" name="name" value={formData.name} placeholder="Name as per records" onChange={handleChange} required />
                        </div>
                    </div>
                    
                    <div className="input-group-custom full-width">
                        <label htmlFor="email">College Email / Personal Email</label>
                        <div className="input-wrapper">
                            <i className="fas fa-envelope"></i>
                            <input id="email" type="email" name="email" value={formData.email} placeholder="cadet@kskcc.edu.pk" onChange={handleChange} required />
                        </div>
                    </div>

                    <div className="input-group-custom full-width">
                        <label htmlFor="password">Password</label>
                        <div className="input-wrapper">
                            <i className="fas fa-lock" style={{color: passwordError ? '#d90429' : ''}}></i>
                            <input 
                                id="password" 
                                type="password" 
                                name="password" 
                                value={formData.password}
                                className={passwordError ? "input-error" : ""} 
                                placeholder="uppercase, lowercase, special ch, number" 
                                onChange={handleChange} 
                                required 
                            />
                        </div>
                        {passwordError && (
                            <div className="password-error-text">
                                <i className="fas fa-exclamation-circle"></i> {passwordError}
                            </div>
                        )}
                    </div>

                    <div className="input-group-custom">
                        <label htmlFor="kit_number">Kit No. / Membership No.</label>
                        <div className="input-wrapper">
                            <i className="fas fa-id-badge"></i>
                            <input id="kit_number" type="text" name="kit_number" value={formData.kit_number} placeholder="e.g. 1234" onChange={handleChange} required />
                        </div>
                    </div>

                    <div className="input-group-custom">
                        <label htmlFor="contact_no">Contact No.</label>
                        <div className="input-wrapper">
                            <i className="fas fa-phone"></i>
                            <input id="contact_no" type="text" name="contact_no" value={formData.contact_no} placeholder="e.g. 0300-1234567" onChange={handleChange} required />
                        </div>
                    </div>

                    <div className="input-group-custom">
                        <label htmlFor="cnic">CNIC Number</label>
                        <div className="input-wrapper">
                            <i className="fas fa-id-card"></i>
                            <input id="cnic" type="text" name="cnic" value={formData.cnic} placeholder="e.g. 12345-1234567-1" onChange={handleChange} required />
                        </div>
                    </div>

                    <div className="input-group-custom">
                        <label htmlFor="wing">Select House</label>
                        <div className="input-wrapper">
                            <i className="fas fa-feather-alt"></i>
                            <select id="wing" name="wing" value={formData.wing} onChange={handleChange} required>
                                <option value="" disabled>Choose your House...</option>
                                <option value="Iqbal">Iqbal House</option>
                                <option value="Jinnah">Jinnah House</option>
                                <option value="Qasim">Qasim House</option>
                                <option value="Johar">Johar House</option>
                                <option value="Tipu">Tipu House</option>
                                <option value="Khalid">Khalid House</option>
                            </select>
                        </div>
                    </div>

                    <div className="input-group-custom">
                        <label htmlFor="entry_year">Entry Year</label>
                        <div className="input-wrapper">
                            <i className="fas fa-calendar-alt"></i>
                            <input id="entry_year" type="number" name="entry_year" value={formData.entry_year} placeholder="e.g. 2018" onChange={handleChange} required />
                        </div>
                    </div>

                    <div className="input-group-custom">
                        <label htmlFor="entry_batch">Entry / Batch</label>
                        <div className="input-wrapper">
                            <i className="fas fa-layer-group"></i>
                            <input id="entry_batch" type="text" name="entry_batch" value={formData.entry_batch} placeholder="e.g. 1st Special Entry" onChange={handleChange} required />
                        </div>
                    </div>
                </div>

                <button type="submit" className="login-submit-btn">
                    Continue <i className="fas fa-arrow-right ms-2"></i>
                </button>
              </>
          )}

          {step === 2 && (
              <>
                <div className="payment-box">
                    {/* Dynamic Floating Badge */}
                    <div className="premium-price-badge">
                        <span className="currency">PKR</span>{paymentDetails.fee}
                    </div>

                    <h6 className="fw-bold mb-4 text-center" style={{color: '#134628', fontSize: '1.1rem'}}>
                        <i className="fas fa-wallet me-2" style={{color: '#ffc300'}}></i>
                        Registration Fee
                    </h6>
                    
                    {/* 🌟 NEW: Beautifully structured inner card for payment details */}
                    <div className="p-3 mb-4 text-start" style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e1e4e8', boxShadow: '0 4px 10px rgba(0,0,0,0.03)' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{ borderBottom: '1px dashed #e1e4e8' }}>
                            <span className="text-muted" style={{fontSize: '0.85rem', fontWeight: '700'}}>
                                <i className="fas fa-mobile-alt me-2"></i>Easypaisa No.
                            </span>
                            <span className="fw-bolder" style={{color: '#134628', fontSize: '1.05rem', letterSpacing: '0.5px'}}>
                                {paymentDetails.number}
                            </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted" style={{fontSize: '0.85rem', fontWeight: '700'}}>
                                <i className="fas fa-user-tag me-2"></i>Account Title
                            </span>
                            <span className="badge rounded-pill" style={{background: '#134628', color: '#ffc300', fontSize: '0.8rem', padding: '6px 15px', fontWeight: '700'}}>
                                {paymentDetails.title}
                            </span>
                        </div>
                    </div>

                    <div className="input-group-custom text-start">
                        <label htmlFor="payment_screenshot" style={{fontSize: '0.85rem'}}>Attach Payment Screenshot <span className="text-danger">*</span></label>
                        <div className="input-wrapper" style={{background: '#fff', borderRadius: '12px', border: '2px solid #e1e4e8'}}>
                            <i className="fas fa-file-image" style={{left: '12px', zIndex: 2}}></i>
                            <input 
                                id="payment_screenshot" 
                                type="file" 
                                accept="image/*" 
                                onChange={handleFileChange} 
                                required 
                                style={{padding: '10px 15px 10px 40px', border: 'none', background: 'transparent'}}
                            />
                        </div>
                        <small className="d-block mt-2 text-center" style={{color: '#6c757d', fontSize: '0.75rem', lineHeight: '1.4'}}>
                            Your account will remain on hold until Admin verifies this transaction.
                        </small>
                    </div>
                </div>

                <button type="submit" className="login-submit-btn" disabled={loading}>
                    {loading ? (
                        <><i className="fas fa-circle-notch fa-spin me-2"></i> Processing...</>
                    ) : (
                        <>Complete Registration <i className="fas fa-check-circle ms-2"></i></>
                    )}
                </button>
                
                <button type="button" className="btn-back" onClick={() => setStep(1)} disabled={loading}>
                    <i className="fas fa-arrow-left me-2"></i> Back to Details
                </button>
              </>
          )}

        </form>

        <div className="login-footer">
          <span>Already a member?</span>
          <a href="/login">Sign In here</a>
        </div>
      </div>
    </div>
  );
}

export default Register;